import { compose } from 'recompose';
import { useTranslation } from 'react-i18next';
import { useState, useEffect } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import moment from 'moment';
import isEmpty from 'lodash/isEmpty';
import { get, post } from 'helpers/apiHelpers';
import { toast } from 'react-toastify';

const MenuCopyingContainer = Component => {
  const WrappedComponent = () => {
    const { t } = useTranslation();

    const dateFormat = 'YYYY-MM-DD';

    const [targets, setTargets] = useState({});
    const [results, setResults] = useState([]);
    const [isCopying, setIsCopying] = useState(false);
    const [copyFromPublished, setCopyFromPublished] = useState(false);
    const [publishMenuAfterCopy, setPublishMenuAfterCopy] = useState(false);
    const [sourceBrand, setSourceBrand] = useState({});
    const [dateRangeTo, setDateRangeTo] = useState(new Date());
    const [targetBrands, setTargetBrands] = useState([]);
    const [brandsOptions, setBrandsOptions] = useState([]);
    const [dateRangeFrom, setDateRangeFrom] = useState(new Date());
    const [sourceDietOptions, setSourceDietOptions] = useState([]);
    const [sourceVariantOptions, setSourceVariantOptions] = useState([]);
    const [isResultDialogOpened, setIsResultDialogOpened] = useState(false);
    const [sourceMealTypeOptions, setSourceMealTypeOptions] = useState([]);
    const [otherDateRangeFrom, setOtherDateRangeFrom] = useState(null);
    const [otherDateRangeTo, setOtherDateRangeTo] = useState(null);
    const [isMenuOnOtherDate, setIsMenuOnOtherDate] = useState(false);

    const { brands, selectedBrand } = useSelector(
      ({
        Auth: {
          // eslint-disable-next-line no-shadow -- FIXME
          user: { brands },
          // eslint-disable-next-line no-shadow -- FIXME
          selectedBrand,
        },
      }) => ({ brands, selectedBrand }),
      shallowEqual
    );

    useEffect(() => {
      Promise.all([
        get('/meal-types', {
          pagination: false,
        }),
        get('/diets', {
          pagination: false,
        }),
        get('/variants', {
          pagination: false,
        }),
        // eslint-disable-next-line promise/prefer-await-to-then -- FIXME
      ]).then(res => {
        const { 'hydra:member': sourceMealTypes } = res[0];
        const { 'hydra:member': sourceDiets } = res[1];
        const { 'hydra:member': sourceVariants } = res[2];

        setSourceDietOptions(sourceDiets);
        setSourceVariantOptions(sourceVariants);
        setSourceMealTypeOptions(sourceMealTypes);
        setBrandsOptions(
          brands.map(brand => ({ ...brand, value: brand?.name }))
        );
        setSourceBrand(brands.find(({ id }) => id === selectedBrand));
      });
      // eslint-disable-next-line react-hooks/exhaustive-deps -- FIXME
    }, []);

    // When selecting brands it updates object format of targets (out of which is prepared payload on submit)
    useEffect(() => {
      let newTargets = {};

      (targetBrands ?? []).map(targetBrand => {
        const targetBrandSettings = targets?.[targetBrand?.['@id']];
        newTargets = {
          ...newTargets,
          [targetBrand?.['@id']]: {
            ...targetBrandSettings,
          },
        };
        return null;
      });

      setTargets(newTargets);
      // eslint-disable-next-line react-hooks/exhaustive-deps -- FIXME
    }, [targetBrands]);

    const handleSubmit = () => {
      const targetsPayload = [];

      for (const [key, value] of Object.entries(targets)) {
        const { dietMappings, mealTypeMapping } = value;
        if (!isEmpty(dietMappings) || !isEmpty(mealTypeMapping)) {
          const dietMappingsPayload = [];
          const mealTypeMappingPayload = [];

          if (!isEmpty(dietMappings)) {
            // eslint-disable-next-line no-shadow -- FIXME
            for (const [key, value] of Object.entries(dietMappings)) {
              const { variantMappings } = value;
              const variantMappingsPayload = [];

              if (!isEmpty(variantMappings)) {
                // eslint-disable-next-line no-shadow -- FIXME
                for (const [key, value] of Object.entries(variantMappings)) {
                  variantMappingsPayload.push({
                    source: key,
                    target: value?.['@id'],
                  });
                }
              }

              let newDietMapping = {
                source: key,
                target: value?.targetDiet?.['@id'],
              };

              if (!isEmpty(variantMappingsPayload)) {
                newDietMapping = {
                  ...newDietMapping,
                  variantMappings: variantMappingsPayload,
                };
              }

              dietMappingsPayload.push(newDietMapping);
            }
          }

          if (!isEmpty(mealTypeMapping)) {
            // eslint-disable-next-line no-shadow -- FIXME
            for (const [key, value] of Object.entries(mealTypeMapping)) {
              mealTypeMappingPayload.push({
                source: key,
                target: value?.['@id'],
              });
            }
          }

          targetsPayload.push({
            target: key,
            dietMappings: dietMappingsPayload,
            mealTypeMapping: mealTypeMappingPayload,
          });
        }
      }

      // eslint-disable-next-line no-use-before-define -- FIXME
      if (!validateTargetsPayload(targetsPayload)) {
        toast.warning(t('$*form.menuPlanning.copyingFailed'));
        return;
      }

      setIsResultDialogOpened(true);
      setIsCopying(true);

      post(
        '/menu-planners/copy',
        {
          source: sourceBrand?.['@id'],
          // eslint-disable-next-line new-cap -- FIXME
          dateRangeFrom: new moment(dateRangeFrom).format(dateFormat),
          // eslint-disable-next-line new-cap -- FIXME
          dateRangeTo: new moment(dateRangeTo).format(dateFormat),
          ...(otherDateRangeFrom && { targetDateFrom: otherDateRangeFrom }),
          copyFromPublished,
          publishMenuAfterCopy,
          targets: targetsPayload,
        },
        {
          params: {
            selectInBrands: [
              sourceBrand?.['@id'],
              ...targetBrands.map(({ '@id': iri }) => iri),
            ],
          },
        }
      )
        // eslint-disable-next-line promise/prefer-await-to-then -- FIXME
        .then(res => {
          setIsCopying(false);
          setResults(res?.['hydra:member'] ?? []);
        })
        // eslint-disable-next-line promise/prefer-await-to-then, promise/prefer-await-to-callbacks, @typescript-eslint/no-unused-vars -- FIXME
        .catch(err => {
          setIsCopying(false);
          setIsResultDialogOpened(false);
        });
    };

    const validateTargetsPayload = targetsPayload =>
      !targetsPayload.some(
        ({ dietMappings, mealTypeMapping }) =>
          isEmpty(dietMappings) ||
          isEmpty(mealTypeMapping) ||
          dietMappings.some(({ variantMappings }) => isEmpty(variantMappings))
      );

    return (
      <Component
        targets={targets}
        results={results}
        isCopying={isCopying}
        setTargets={setTargets}
        sourceBrand={sourceBrand}
        dateRangeTo={dateRangeTo}
        targetBrands={targetBrands}
        handleSubmit={handleSubmit}
        dateRangeFrom={dateRangeFrom}
        brandsOptions={brandsOptions}
        setDateRangeTo={setDateRangeTo}
        setTargetBrands={setTargetBrands}
        setDateRangeFrom={setDateRangeFrom}
        copyFromPublished={copyFromPublished}
        sourceDietOptions={sourceDietOptions}
        publishMenuAfterCopy={publishMenuAfterCopy}
        setCopyFromPublished={setCopyFromPublished}
        sourceVariantOptions={sourceVariantOptions}
        sourceMealTypeOptions={sourceMealTypeOptions}
        isResultDialogOpened={isResultDialogOpened}
        setIsResultDialogOpened={setIsResultDialogOpened}
        setPublishMenuAfterCopy={setPublishMenuAfterCopy}
        isMenuOnOtherDate={isMenuOnOtherDate}
        setIsMenuOnOtherDate={setIsMenuOnOtherDate}
        otherDateRangeFrom={otherDateRangeFrom}
        setOtherDateRangeFrom={setOtherDateRangeFrom}
        otherDateRangeTo={otherDateRangeTo}
        setOtherDateRangeTo={setOtherDateRangeTo}
      />
    );
  };

  return WrappedComponent;
};

export default compose(MenuCopyingContainer);
